import { isValidElement, useState } from 'react';

import ReactSelect, {
  Props,
  GroupBase,
  ActionMeta,
  components,
  MultiValue,
  SingleValue,
  OptionProps,
  OnChangeValue,
} from 'react-select';

import { randomId } from 'utils';
import TooltipPopover, { TooltipType } from './TooltipPopover';

// SORT DROPDOWN OPTIONS ARRAY
export const sortOptions = {
  ascending: (a: SelectOption, b: SelectOption) => {
    const labelA = a.label.toLowerCase();
    const labelB = b.label.toLowerCase();

    if (labelA < labelB) {
      return -1;
    }
    if (labelA > labelB) {
      return 1;
    }
    return 0;
  },
  descending: (a: SelectOption, b: SelectOption) => {
    const labelA = a.label.toLowerCase();
    const labelB = b.label.toLowerCase();

    if (labelA < labelB) {
      return 1;
    }
    if (labelA > labelB) {
      return -1;
    }
    return 0;
  },
};

// DROPDOWN OPTIONS SORTER
export const sortSelectOptions = <T extends SelectOption>(
  options: T[],
  order: 'asc' | 'desc' = 'asc'
) => {
  const output = [...options];

  return order === 'asc'
    ? output.sort(sortOptions.ascending)
    : output.sort(sortOptions.descending);
};

export interface SelectOption<V extends string | number = string> {
  label: string;
  value: V;
  isDisabled?: boolean;
}

export type SingleSelection<T = SelectOption> = SingleValue<T>;
export type MultiSelection<T = SelectOption> = MultiValue<T> | null;

export interface SelectProps {
  label?: string;
  invert?: boolean;
  truncate?: boolean;
  singleLine?: boolean;
  tooltipType?: TooltipType;
  tooltip?: string | React.ReactElement;
  handleAutoSave?: () => void;
}

// SELECT (DEFAULT === SINGLE SELECT)
function Select<
  IsMulti extends boolean = false,
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  label,
  invert,
  options,
  value,
  isMulti,
  tooltip,
  onChange,
  truncate = false,
  singleLine = false,
  tooltipType = 'info',
  handleAutoSave,
  ...props
}: Props<Option, IsMulti, Group> & SelectProps) {
  const [inputError, setInputError] = useState<string | null>(null);
  function Option(props: OptionProps<Option, IsMulti, Group>) {
    return (
      <div title={props.label}>
        <components.Option {...props} />
      </div>
    );
  }

  const id = randomId('select');

  const onSelect = (
    newValue: OnChangeValue<Option, IsMulti>,
    actionMeta: ActionMeta<Option>
  ) => {
    if (onChange) {
      onChange(newValue, actionMeta);
    }

    if (newValue && inputError) {
      setInputError(null);
    }
  };

  const onValidate = () => {
    if (props.required && !value) {
      setInputError('Required');
    }
  };

  // JSX
  return (
    <div className="flex flex-col gap-1">
      {tooltip && !label ? (
        <TooltipPopover
          type={tooltipType}
          tooltipWidth="max-w-96"
          className="absolute top-0 -right-4"
          content={typeof tooltip === 'string' ? tooltip : undefined}
          complexContent={isValidElement(tooltip) ? tooltip : undefined}
        />
      ) : tooltip && label ? (
        <div className="w-fit relative">
          <TooltipPopover
            type={tooltipType}
            tooltipWidth="max-w-96"
            className="absolute top-0 -right-4 max-w-40"
            content={typeof tooltip === 'string' ? tooltip : undefined}
            complexContent={isValidElement(tooltip) ? tooltip : undefined}
          >
            <label className="font-semibold leading-tight" htmlFor={id}>
              {label}
              {props.required && (
                <span className="ms-0.5 text-status-red-500">*</span>
              )}
            </label>
          </TooltipPopover>
        </div>
      ) : label && !tooltip ? (
        <label className="font-semibold leading-tight" htmlFor={id}>
          {label}
          {props.required && (
            <span className="ms-0.5 text-status-red-500">*</span>
          )}
        </label>
      ) : null}
      <ReactSelect
        inputId={id}
        options={options}
        onBlur={() => {
          onValidate();
        }}
        styles={{
          control: (base, state) => ({
            ...base,
            fontSize: '15px',
            lineHeight: '18px',
            background: invert ? '#333333' : base.background,
            borderWidth: '1px',
            borderColor: invert ? 'transparent' : '#cccccc',
            boxShadow: 'none',
            pointerEvents: 'auto',
            ':hover': {
              cursor: state.isDisabled ? 'not-allowed' : 'pointer',
              borderColor: invert ? 'transparent' : '#cccccc',
            },
          }),

          // DROPDOWN ICON STYLES
          dropdownIndicator: (base, state) => {
            let color = base.color;

            if (invert && state.isDisabled) {
              color = '#999999';
            } else if (invert) {
              color = '#B3B3B3';
            }
            return {
              ...base,
              color,
            };
          },

          // PLACEHOLDER STYLES
          placeholder: (base, state) => ({
            ...base,
            color: state.isDisabled
              ? '#999999'
              : invert
              ? '#B3B3B3'
              : state.hasValue
              ? 'black'
              : base.color,
          }),

          // SINGLE VALUE STYLES
          singleValue: (base, state) => ({
            ...base,
            fontSize: '15px',
            color: state.isDisabled ? '#999999' : invert ? 'white' : 'black',
            ':hover': {
              cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            },
          }),

          // MULTI VALUE STYLES
          multiValue: (base, state) => ({
            ...base,
            fontSize: '15px',
            color: state.isDisabled ? '#999999' : invert ? 'white' : 'black',
          }),

          // INDICATOR SEPARATOR STYLES
          indicatorSeparator: () => ({}),

          // MENU STYLES
          menu: (base) => ({
            ...base,
            fontSize: '15px',
            background: invert ? '#333333' : base.background,
          }),

          // MENU LIST STYLES
          menuList: (base) => ({
            ...base,
            scrollbarWidth: 'thin',
          }),

          groupHeading: (base) => ({
            ...base,
            fontSize: '14px',
            fontWeight: 'bold',
            textTransform: 'capitalize',
          }),

          // OPTION STYLES
          option: (base, state) => ({
            ...base,
            fontSize: '15px',
            overflow: truncate && !singleLine ? 'hidden' : base.overflow,
            textOverflow: 'ellipsis',
            textWrap: truncate || singleLine ? 'nowrap' : base.textWrap,
            background: !state.isSelected
              ? 'none'
              : invert
              ? '#1a1a1a'
              : '#E6E6E6',
            ':hover': {
              background: invert ? '#222222' : '#E6E6E6',
            },
            minWidth: singleLine ? 'max-content' : base.minWidth,
            color: state.isDisabled ? '#CCCCCC' : invert ? 'white' : 'black',
          }),
        }}
        onChange={(newValue, actionMeta) => {
          onSelect(newValue, actionMeta);
          handleAutoSave?.();
        }}
        value={value}
        isMulti={isMulti}
        hideSelectedOptions={false}
        controlShouldRenderValue={!isMulti}
        closeMenuOnSelect={!isMulti}
        {...props}
        components={{
          Option,
        }}
      />
      {inputError ? (
        <span className="text-error text-sm leading-none">{inputError}</span>
      ) : null}
    </div>
  );
}

export default Select;
